import {
  BANDS_DJS_TAXONOMY_KEY,
  BAR_SERVICES_TAXONOMY_KEY,
  CAKES_DESSERTS_TAXONOMY_KEY,
  CATERING_TAXONOMY_KEY,
  FLORISTS_TAXONOMY_KEY,
  HAIR_MAKEUP_TAXONOMY_KEY,
  PHOTOGRAPHERS_TAXONOMY_KEY,
  PLANNERS_TAXONOMY_KEY,
  SearchableVendorTaxonomyKey,
  VENUES_TAXONOMY_KEY,
  VIDEOGRAPHERS_TAXONOMY_KEY,
} from '@zola-helpers/client/dist/es/marketplace/vendorTaxonomyKeys';
import { getVendorWordCategory } from '@zola-helpers/client/dist/es/marketplace/vendorWords';

import { FaqContent } from '~/types/types';

export interface FaqObject {
  title: string | null;
  content: FaqContent[];
}

const VenuesFAQ: FaqObject = {
  title: getVendorWordCategory(VENUES_TAXONOMY_KEY),
  content: [
    {
      question: 'How much do wedding & reception venues cost?',
      answer: `<p>The cost of wedding venues varies, with <a href="/expert-advice/how-much-do-wedding-venues-cost">factors influencing cost</a> including size, location, guest count, and, most importantly, whether things like
      catering and rentals are included. The more a reception venue near you includes, the higher the cost. When considering venues, try to compare prices apples-to-apples. If
      one venue includes catering and rentals in their pricing and the other doesn’t, you need to figure out how much additional cost those outside vendors
      would be to make a true comparison. Before booking, be sure you’re aware of additional costs outlined in your contract, like parking fees and
      weather-related considerations (adding a tent, etc.).</p>`,
    },
    {
      question: 'What questions should we ask wedding venues?',
      answer: `<p>When talking with a potential venue, <a href="/expert-advice/questions-to-ask-when-touring-a-wedding-venue">always ask</a> about 1) their availability and 2) their capacity. Some popular venues book up peak season Saturdays nearly
      2 years in advance—if your first choice date isn’t available, consider your flexibility on day of the week and year. While maximum capacity is non-negotiable,
      venues might have guidance on what guest count will be most comfortable. Also, you’ll want to understand what is and is not included in the venue’s fees.
      Some include catering, tables and chairs, and linens, while others offer just the raw space. These venues are often less expensive because you are only paying for
      the space itself, and you’ll have to bring in outside vendors</p>`,
    },
  ],
};

const PhotographersFAQ = {
  title: getVendorWordCategory(PHOTOGRAPHERS_TAXONOMY_KEY),
  content: [
    {
      question: 'How much does a wedding photographer cost?',
      answer: `Wedding photographers early in their careers can cost as little as $1,500. Super experienced photographers, as seen in magazines like Vogue and Vanity Fair,
      can cost upwards of $30,000. The range is wide, but, in general, you can <a href="/expert-advice/how-much-does-a-wedding-photographer-cost">expect to spend around 10% of your budget</a> on wedding photography. Rates also vary based on
      wedding length, the services you want, and location. A photographer based in an area with a lower cost of living, for example, will be less than one based in New York
      or San Francisco.`,
    },
    {
      question: 'How much should we tip our wedding photographer?',
      answer: `A <a href="/expert-advice/should-you-tip-wedding-photographers">good rule of thumb</a> is to tip 10% of their rate, or at least $100. If they’re bringing second photographers or assistants, an additional $50 to $75 per person
      is an appropriate and nice gesture.`,
    },
    {
      question: 'What questions should we ask wedding photographers?',
      answer: `Aside from logistical questions about packages, pricing, and availability, it’s also important to use your conversation to get a sense of their personality.
      This person will be with you and your loved ones every step of your wedding day, including, if you want, in the dressing room! It’s very important that you and your
      partner both feel comfortable with them and their working style. Talk about your venue, how you want to feel when you look at your photos, and which photos from their
      portfolio you like best.`,
    },
  ],
};

const VideographersFAQ = {
  title: getVendorWordCategory(VIDEOGRAPHERS_TAXONOMY_KEY),
  content: [
    {
      question: 'What questions should we ask wedding videographers?',
      answer: `Aside from logistical <a href="/expert-advice/10-questions-to-ask-your-wedding-videographer">questions</a> about packages, pricing, and availability, it’s also important to use your conversation to get a sense of their personality.
      This person will be with you and your loved ones every step of your wedding day, including, if you want, in the dressing room! It's very important that you and your
      partner both feel comfortable with them and their working style. Talk about your venue, how you want to feel when you look at your video, and which videos from their
      portfolio you like best. If you have a photographer already, also ask if they’ve worked with that photographer before, or how they work with photographers generally—both
      will be trying to capture the same moments!`,
    },
    {
      question: 'How much should we tip our wedding videographer?',
      answer: `A good tip ranges <a href="/expert-advice/should-you-tip-wedding-videographers">anywhere from $50 to $100</a>—perhaps even more if they brought an assistant. If you’re using a larger company, the tip may be included in your
      contract or fees. Uncertain whether to budget in a tip? You can always wait until you receive your final video, then send a gift or a tip as thanks.`,
    },
  ],
};

const PlannersFAQ = {
  title: getVendorWordCategory(PLANNERS_TAXONOMY_KEY),
  content: [
    {
      question: 'How much does a wedding planner cost?',
      answer: `On average, a full-service wedding planner can cost between $3,000 and $10,000. It depends on where you live and where you’re getting married. Planners in areas
      with high costs of living, like New York City and San Francisco, may charge upwards of $20,000. If you’re having a destination wedding, consider using a planner local
      to the area—you’ll avoid paying extra for travel, accommodations, and meals. Day-of coordinators cost less. They may have a flat fee between $1,000 and $5,000, or charge
      hourly. Always ask about pricing up front so nothing comes as a surprise later.`,
    },
    {
      question: 'What do wedding planners do?',
      answer: `
      Think of wedding planners as jack-of-all-trades experts when it comes to weddings. Many offer different levels of service to meet your needs. If you just want
      day-of coordination, for example, your planner will be there to make sure your ceremony and reception go off without a hitch. This may mean setting up vendors or
      directing your wedding party and guests to make sure everyone is where they need to be at the right time. On the other end of the spectrum, full-service planning
      may involve:
      <ul>
        <li>Starting the planning far in advance</li>
        <li>Budget management</li>
        <li>Coordinating with your venue</li>
        <li>Recommending vendors and negotiating contracts</li>
        <li>Staying on top of trends and making suggestions</li>
        <li>Ironing out all-important last-minute details</li>
        <li>Running interference with family members if conflict arises</li>
        <li>Making a schedule and ensuring everything runs smoothly</li>
        <li>Executing on your vision, from the rehearsal dinner to the reception</li>
      </ul>
      `,
    },
    {
      question: 'Should we get a wedding planner? ',
      answer: `<a href="/expert-advice/wedding-planner">Planners are great resources</a> and can make planning your wedding much less stressful. If you’ve got the budget, a wedding planner is well worth the
      investment. Planners offer different service levels at varying price points. Choose the amount of help and cost that makes the most sense for you. At the
      bare minimum, you need someone (hired or delegated) to help manage the event day-of so you will be able to enjoy yourselves without worrying about too
      many logistics. If you decide to enlist a friend or family member to take on this role, keep in mind that this person will need to prioritize your needs
      over their own, and will no longer be attending or enjoying the wedding simply as a guest. `,
    },
  ],
};

const BandsAndDJsFAQ = {
  title: getVendorWordCategory(BANDS_DJS_TAXONOMY_KEY),
  content: [
    {
      question: 'How much does a wedding DJ or band cost?',
      answer: `Expect to spend around 10–15% of your total budget on musical entertainment. <a href="/expert-advice/how-much-does-a-live-wedding-band-cost">Compared to live bands</a> (or any other live entertainment), DJs generally cost
      less. DJs travel with less equipment and often work solo, whereas bands must lug multiple instruments and can have up to several members.`,
    },
    {
      question: 'What should we ask potential wedding bands or DJs?',
      answer: `<a href="/expert-advice/how-to-choose-your-wedding-dj">Ask about</a> their experience, pricing, and availability. You also should review videos of past performances and ask about their favorite music to
      play—while most bands and DJs can play many styles, they are often at their best doing what they really love. Finally, be sure to ask about important
      logistics, like set length, and what they plan to play between sets.`,
    },
    {
      question: 'How do we find potential wedding bands or DJs?',
      answer: `Right here! Zola has a great collection of bands and DJs, no matter your style. Search by price, location, availability, and more. You can even reach out right on Zola.`,
    },
  ],
};

const FloristsFAQ = {
  title: getVendorWordCategory(FLORISTS_TAXONOMY_KEY),
  content: [
    {
      question: 'How much do wedding bouquets cost?',
      answer: `Wedding bouquets vary in cost by bouquet size, quantity and density of blooms, season, and flower type. Your wedding location and cost of living will also
      have an impact. In general, you can expect to pay $200–$400 for a larger main bouquet, and $100–$250 for smaller wedding party bouquets. Communicate openly with
      your florist about your budget and vision—colors and inspirational photos are often more helpful to your florist than specific types of flowers.`,
    },
    {
      question: 'How can we save money on our wedding flowers?',
      answer: `Couples are often surprised by the amount of their budget spent on wedding flowers. Labor and variable wholesale costs add up quickly!
      To save, avoid anything that needs to be installed day-of and stick to “wear and carry.” This means the florist simply drops off the arrangements
      (bouquets, boutineers, centerpieces, etc.) at your venue for you to place yourselves. Something like a ceremony arch, by contrast, requires multiple people
      to set up and take down on site, often with a service charge of up to 40%. Talk openly with your florist about the look you want to achieve, rather than specific
      flowers you want. They can suggest flower species and options to make your vision happen in a way that fits your budget.`,
    },
  ],
};

const CaterersFAQ = {
  title: getVendorWordCategory(CATERING_TAXONOMY_KEY),
  content: [
    {
      question: 'How much does wedding catering cost?',
      answer: `When thinking about <a href="/expert-advice/cost-of-wedding-caterers">catering cost</a>, consider drinks, hors d'oeuvres and stations, and the main meal itself. Bar services are quoted per person and
      pricing often depends on what alcohol you want (more expensive packages include pricier labels). Hors d'oeuvres and stations are also priced per person,
      with variable costs for more expensive options like lobster rolls. For the main meal, consider what you’d spend per head going out to a restaurant in your
      area—the cost per person at your wedding will be similar. Keep in mind that plated meals cost more per person than buffets.`,
    },
    {
      question: 'When should we book our wedding caterer?',
      answer: `Choosing a wedding caterer is an important decision for many couples—good food can really make an event stellar! It’s best to <a href="/expert-advice/exactly-how-far-out-to-book-each-wedding-vendor">start researching caterers
      10–12 months out</a>, and book around 8 months out. You may not always be able to schedule a tasting before booking your caterer. For many smaller vendors, the effort
      and expense to put together tastings without a signed contract is prohibitive. In these cases, your tasting will take place after you’ve booked the caterer and will
      focus on refining menu choices.`,
    },
  ],
};

const CakesDessertsFAQ = {
  title: getVendorWordCategory(CAKES_DESSERTS_TAXONOMY_KEY),
  content: [
    {
      question: 'How much do wedding cakes cost?',
      answer: `Wedding cakes can range from <a href="/expert-advice/a-guide-to-wedding-cake-costs">$3–$15 per serving</a>. Tiered cakes are more expensive than sheet cake styled cakes.`,
    },
    {
      question: 'How should we cut our wedding cake?',
      answer: `Traditionally, you’d do a <a href="/expert-advice/wedding-cake-traditions-and-their-significance">ceremonial cutting</a> first, often holding hands on the knife. The caterer then takes the cake away and cuts it into slices
      to distribute to guests. If you’re slicing it up yourselves, take apart the tiers and cut them into small rectangles. Remember to wrap and freeze the top
      layer for your anniversary!`,
    },
    {
      question: 'How should we transport our wedding cake?',
      answer: `Very carefully! Ideally, your cake will be delivered by your bakery, which should have plenty of experience transporting cakes. You can also purchase special
      boxes made to protect the tiers.`,
    },
    {
      question: 'When should we order our wedding cake?',
      answer: `Set up tastings (the fun part!) around 7 months out. Once you’ve decided, place your order.`,
    },
  ],
};

const BarServicesFAQ = {
  title: getVendorWordCategory(BAR_SERVICES_TAXONOMY_KEY),
  content: [
    {
      question: 'How much does an open bar at a wedding cost?',
      answer: `
      <a href="/expert-advice/how-much-should-you-budget-for-an-open-bar-at-your-wedding">Price per person can vary</a> based on your location, venue, and alcohol package. On average, cost per person is $11– $45 a head. This changes depending on
      the alcohol you choose:
      <ul>
        <li>Full open bar: $35–$45 per person</li>
        <li>Limited open bar: $20–$25 per person</li>
        <li>Beer and wine only: $15–20 per person</li>
      </ul>
      `,
    },
    {
      question: 'How much should we tip the bartender at our wedding?',
      answer: `A good general rule of thumb is to <a href="/expert-advice/how-much-do-you-tip-your-wedding-bartender">tip 18–25%</a>. If they provide really exceptional service, a nice tip is the best way to show your appreciation.`,
    },
    {
      question: 'What drinks should we serve at our wedding?',
      answer: `Many people choose to serve wine, beer, champagne or sparking wine, and liquor. <a href="/expert-advice/the-ultimate-wedding-alcohol-list-power-ranking-the-best-adult-wedding">Specialty cocktails</a> are also a fun way to personalize your wedding even more.
      Make sure to have some non-alcoholic options available for guests who don’t drink, too.`,
    },
    {
      question: 'What is a cash bar at a wedding?',
      answer: `Cash bars require guests to purchase their own drinks, saving you a significant amount of money. You can also partially subsidize drinks, transition
      to a cash bar after a certain point in the event, or require payment for liquor while keeping wine and beer free.`,
    },
  ],
};

const BeautyServicesFAQ = {
  title: getVendorWordCategory(HAIR_MAKEUP_TAXONOMY_KEY),
  content: [
    {
      question: 'How much does hair and makeup cost for a wedding?',
      answer: `According to Thumbtack, <a href="/expert-advice/tips-for-working-with-a-wedding-makeup-artist">wedding makeup artists</a> cost $90–$475 per person on average in 2022. Here at Zola, we recommend putting aside around $300 for your own
      wedding hair and makeup. Services for wedding party members are generally less expensive, as the artist or stylist will spend less time perfecting the look. Be sure
      to have a transparent conversation with your wedding party if you expect them to cover their own costs.`,
    },
    {
      question: 'How long does wedding hair and makeup take to do?',
      answer: `The more involved the look, the more time it will take. In general, <a href="/expert-advice/how-long-does-wedding-hair-and-makeup-usually-take">allocate 60–90 minutes for your own looks</a> and 30–45 minutes for each member of the
      wedding party. Extensions, extra thick hair, and intricate makeup all take longer. Communicating who (with pictures and descriptions) is getting what
      (again, with photos) to your stylists will help them plan an accurate timeline.`,
    },
    {
      question: 'Who usually pays for hair and makeup for the wedding party?',
      answer: `Traditionally, whoever is asking for the services should pay. However, this may not be in the budget for every couple. If you’re planning on having your
      wedding party pay, be transparent when you first ask them to be in the wedding party itself. Then, they can choose whether it’s possible for them to participate.`,
    },
    {
      question: 'How much should we tip for our wedding hair and makeup artists?',
      answer: `A good rule of thumb is to tip 15–20%.`,
    },
  ],
};

export const VendorsFAQ: Record<SearchableVendorTaxonomyKey, FaqObject> = {
  [VENUES_TAXONOMY_KEY]: VenuesFAQ,
  [PHOTOGRAPHERS_TAXONOMY_KEY]: PhotographersFAQ,
  [VIDEOGRAPHERS_TAXONOMY_KEY]: VideographersFAQ,
  [PLANNERS_TAXONOMY_KEY]: PlannersFAQ,
  [BANDS_DJS_TAXONOMY_KEY]: BandsAndDJsFAQ,
  [FLORISTS_TAXONOMY_KEY]: FloristsFAQ,
  [CATERING_TAXONOMY_KEY]: CaterersFAQ,
  [CAKES_DESSERTS_TAXONOMY_KEY]: CakesDessertsFAQ,
  [BAR_SERVICES_TAXONOMY_KEY]: BarServicesFAQ,
  [HAIR_MAKEUP_TAXONOMY_KEY]: BeautyServicesFAQ,
};

export default VendorsFAQ;
